import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { spawnSync } from "node:child_process";

export interface ThreeWayResult {
  ok: boolean;
  merged: string;
}

/** Heuristic text check: reject NUL bytes (git treats these as binary). */
export function isProbablyText(s: string): boolean {
  return !s.includes("\0");
}

/**
 * Three-way merge via system git. `-p` prints the result to stdout (ours is
 * never mutated). Exit 0 → clean; >0 → conflict. Binary content on any side is
 * refused up front (never fed to git merge-file). Inputs normalized to LF.
 */
export function mergeThreeWay(
  ours: string,
  base: string,
  theirs: string
): ThreeWayResult {
  if (![ours, base, theirs].every(isProbablyText)) {
    return { ok: false, merged: "" };
  }

  const lf = (s: string) => s.replace(/\r\n/g, "\n");
  const dir = fs.mkdtempSync(path.join(os.tmpdir(), "seed-merge-"));

  try {
    const o = path.join(dir, "ours");
    const b = path.join(dir, "base");
    const t = path.join(dir, "theirs");

    fs.writeFileSync(o, lf(ours));
    fs.writeFileSync(b, lf(base));
    fs.writeFileSync(t, lf(theirs));

    const res = spawnSync(
      "git",
      ["merge-file", "-p", "--diff3", o, b, t],
      {
        encoding: "utf8",
        maxBuffer: 32 * 1024 * 1024,
      }
    );

    if (res.status === 0) {
      return { ok: true, merged: res.stdout };
    }

    return { ok: false, merged: "" };
  } finally {
    fs.rmSync(dir, { recursive: true, force: true });
  }
}

export type FileVersion = string | null;
export interface Candidate { path: string; base: FileVersion; ours: FileVersion; theirs: FileVersion }
export type MergeKind = "noop" | "update" | "add" | "delete" | "skip";
export interface MergeOutcome { path: string; kind: MergeKind; content?: string; skipReason?: string }

const skip = (path: string, skipReason: string): MergeOutcome => ({ path, kind: "skip", skipReason });

export function classifyCandidate(c: Candidate): MergeOutcome {
  const { path, base, ours, theirs } = c;

  // Seed did not change this file between revs → nothing to deliver.
  if (theirs === base) return { path, kind: "noop" };

  // Seed added a file (absent at base).
  if (base === null) {
    if (theirs === null) return { path, kind: "noop" };          // add-then-remove within window
    if (ours === null) return { path, kind: "add", content: theirs };
    if (ours === theirs) return { path, kind: "noop" };          // founder already has identical content
    return skip(path, "you already have a file at this path");    // collision (e.g. librarian-promoted skill)
  }

  // Founder deleted a seed-owned file (present at base, absent now).
  if (ours === null) {
    // theirs !== base here (guarded above). Never silently resurrect a customer deletion.
    return theirs === null ? { path, kind: "noop" } : skip(path, "you removed this file; upstream changed it");
  }

  // Seed deleted the file (present at base, absent upstream).
  if (theirs === null) {
    return ours === base ? { path, kind: "delete" } : skip(path, "you edited this file; upstream removed it");
  }

  // All three present, theirs != base.
  if (ours === base) return { path, kind: "update", content: theirs };  // founder untouched → fast-forward
  const m = mergeThreeWay(ours, base, theirs);
  return m.ok ? { path, kind: "update", content: m.merged } : skip(path, "your edits conflict with the update");
}
